import { Component, inject, ViewChild, ElementRef, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { Commercial } from '@adgate/modules/tv/types/commecial.type';
import { DialogService } from '@adgate/common/services/dialog-service';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NetworkService } from '@adgate/common/services/network-service';
import { CommonObjectService } from '@adgate/common/services/common-object-service';
import { LogMessage } from '@adgate/common/types/log-message.type';
import { StateService } from '@adgate/common/services/state-service';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-media-player-dialog-component',
  imports: [
    CommonModule,
    MatDialogModule,
    ClipboardModule,
    MatSnackBarModule,
    MatTooltipModule
  ],
  templateUrl: './media-player-dialog-component.html',
  styleUrl: './media-player-dialog-component.scss',
})
export class MediaPlayerDialogComponent implements AfterViewInit, OnDestroy
{
  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;
  @ViewChild('videoPlayerContainer') videoPlayerContainer!: ElementRef<HTMLDivElement>;

  private dialogRef: MatDialogRef<MediaPlayerDialogComponent> = inject(MatDialogRef);
  private dialogService: DialogService = inject(DialogService);
  private networkService: NetworkService = inject(NetworkService);
  private commonObjectService: CommonObjectService = inject(CommonObjectService);
  private stateService: StateService = inject(StateService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private _snackBar: MatSnackBar = inject(MatSnackBar);

  public data: MediaPlayerDialogData = inject(MAT_DIALOG_DATA, { optional: false });

  public isPlaying: boolean = false;
  public isMuted: boolean = false;
  public volume: number = 100;
  public currentTime: number = 0;
  public duration: number = 0;
  public progress: number = 0;
  public showControls: boolean = true;
  private hideControlsTimeout: any;
  private timerId: any;
  private jogOffsetSeconds: number = 1/30;
  private keydownListener: any;

  ngAfterViewInit(): void
  {
    this.currentTime = this.data.startOfMedia;
    this.videoPlayer.nativeElement.src = this.data.mediaUrl;

    this.videoPlayer.nativeElement.addEventListener('play', () => {
      this.timerId = setInterval(() => { this.updateTimecode(); }, 1000/30);
    });

    this.videoPlayer.nativeElement.addEventListener('pause', () => {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    });

    this.videoPlayer.nativeElement.addEventListener('ended', () => {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    });

    // Set up error detection
    this.videoPlayer.nativeElement.addEventListener('error', () => {
      this.onVideoError();
    });

    // Set up keyboard jog controls
    this.keydownListener = (event: KeyboardEvent) => {
      this.jogVideo(event);
    };

    this.videoPlayerContainer.nativeElement.addEventListener('keydown', this.keydownListener, true);
    this.videoPlayerContainer.nativeElement.tabIndex = 0;
    this.videoPlayerContainer.nativeElement.focus();
  }

  onVideoError(): void
  {
    const video = this.videoPlayer.nativeElement;
    const error = video.error;

    if (error) {
      let errorMessage = 'Unknown error';

      switch (error.code) {
        case error.MEDIA_ERR_ABORTED:
          errorMessage = 'Video playback aborted';
          break;
        case error.MEDIA_ERR_NETWORK:
          errorMessage = 'Network error while loading video';
          break;
        case error.MEDIA_ERR_DECODE:
          errorMessage = 'Video decoding failed';
          break;
        case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorMessage = 'Video format not supported';
          break;
      }

      console.error('Video Error:', errorMessage, error);
      this.dialogService.openErrorDialog(errorMessage);
    }
  }

  ngOnDestroy(): void
  {
    if (this.timerId) {
      clearInterval(this.timerId);
    }
    if (this.keydownListener && this.videoPlayerContainer) {
      this.videoPlayerContainer.nativeElement.removeEventListener('keydown', this.keydownListener, true);
    }
  }

  jogVideo(event: KeyboardEvent): void
  {
    let newTime;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      newTime = Math.max(0, this.videoPlayer.nativeElement.currentTime - this.jogOffsetSeconds);

      if (Number.isFinite(newTime)) {
        this.videoPlayer.nativeElement.currentTime = newTime;
        this.updateTimecode();
      }

    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      newTime = Math.min(this.videoPlayer.nativeElement.duration || Infinity, this.videoPlayer.nativeElement.currentTime + this.jogOffsetSeconds);

      if (Number.isFinite(newTime)) {
        this.videoPlayer.nativeElement.currentTime = newTime;
        this.updateTimecode();
      }

    } else if (event.key === ' ') {
      event.preventDefault();

      if (this.videoPlayer.nativeElement.paused) {
        this.videoPlayer.nativeElement.play();
      } else {
        this.videoPlayer.nativeElement.pause();
      }
    }
  }

  private updateTimecode(): void
  {
    const video = this.videoPlayer.nativeElement;
    this.currentTime = video.currentTime + this.data.startOfMedia;
    this.progress = (video.currentTime / video.duration) * 100;
    this.cdr.detectChanges();
  }

  togglePlayPause(): void
  {
    const video = this.videoPlayer.nativeElement;
    if (video.paused) {
      video.play();
      this.isPlaying = true;
    } else {
      video.pause();
      this.isPlaying = false;
    }
  }

  toggleMute(): void
  {
    const video = this.videoPlayer.nativeElement;
    video.muted = !video.muted;
    this.isMuted = video.muted;
  }

  setVolume(event: Event): void
  {
    const target = event.target as HTMLInputElement;
    const video = this.videoPlayer.nativeElement;
    this.volume = parseInt(target.value);
    video.volume = this.volume / 100;
    this.isMuted = this.volume === 0;
  }

  onLoadedMetadata(): void
  {
    const video = this.videoPlayer.nativeElement;
    this.duration = video.duration + this.data.startOfMedia;
  }

  seek(event: MouseEvent): void
  {
    const progressBar = event.currentTarget as HTMLElement;
    const clickX = event.offsetX;
    const width = progressBar.offsetWidth;
    const percentage = clickX / width;
    const video = this.videoPlayer.nativeElement;
    video.currentTime = percentage * video.duration;
    this.updateTimecode();
  }

  toggleFullscreen(): void
  {
    const container = this.videoPlayer.nativeElement.parentElement;
    if (!document.fullscreenElement) {
      container?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  onMouseEnter(): void
  {
    this.showControls = true;
    if (this.hideControlsTimeout) {
      clearTimeout(this.hideControlsTimeout);
    }
  }

  onMouseLeave(): void
  {
    this.showControls = false;
    if (this.hideControlsTimeout) {
      clearTimeout(this.hideControlsTimeout);
    }
  }

  onCopyTimecode(timecode: string)
  {
    this._snackBar.open( `${timecode} copied to clipboard`, '',{duration: 2000});
  }

  formatTime(seconds: number): string
  {
    if (isNaN(seconds)) return '00:00:00:00';

    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const frames = Math.floor((seconds % 1) * 30); // Assuming 30fps

    const hh = hours.toString().padStart(2, '0');
    const mm = mins.toString().padStart(2, '0');
    const ss = secs.toString().padStart(2, '0');
    const ff = frames.toString().padStart(2, '0');

    return `${hh}:${mm}:${ss}:${ff}`;
  }

  onDownloadMedia()
  {
    this.dialogService.showProgress();
    this.networkService.downloadExternalFile(`${this.data.mediaUrl}&stream=false`).subscribe({
      next: (res:Blob) => {
        this.commonObjectService.addObjectHistory(this.data.objectType, [this.data.objectId], this.stateService.getUserName(), "Proxy downloaded", LogMessage.TYPE_SYSTEM, false).subscribe({
          next: () => {},
          error: (err:Error) => {}
        });

        // Infer file extension from content type
        let filename = this.data.identifier;
        const extension = this.getExtensionFromContentType(res.type);

        if (extension && !filename.toLowerCase().endsWith(extension)) {
          filename += extension;
        }

        saveAs(res, filename);
        this.dialogService.hideProgress();
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  private getExtensionFromContentType(contentType: string): string
  {
    const mimeToExt: { [key: string]: string } = {
      'video/mp4': '.mp4',
      'video/quicktime': '.mov',
      'video/x-msvideo': '.avi',
      'video/x-ms-wmv': '.wmv',
      'video/webm': '.webm',
      'video/x-matroska': '.mkv',
      'video/mpeg': '.mpeg',
      'video/x-flv': '.flv',
      'audio/mpeg': '.mp3',
      'audio/wav': '.wav',
      'audio/x-wav': '.wav',
      'audio/aac': '.aac',
      'audio/ogg': '.ogg',
      'image/jpeg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/webp': '.webp'
    };

    return mimeToExt[contentType] || '';
  }

  onCancel(): void
  {
    this.dialogRef.close(null);
  }

  decodeObjectType(): string
  {
    switch( this.data.objectType ) {
      case Commercial.OBJECT_TYPE: return 'commercial';
      default: return '';
    }
  }
}

export interface MediaPlayerDialogData
{
  mediaUrl: string;
  startOfMedia: number;
  identifier: string;
  objectType: number;
  objectId: number;
  mediaDownloadAllowed: boolean;
}

